import { Component, OnInit, PLATFORM_ID, inject, ChangeDetectionStrategy } from '@angular/core';
import { Location, isPlatformBrowser } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthActionCodeService } from '@core/services/auth-action-code.service';
import { AuthFlowService } from '@core/services/auth-flow.service';
import { LoggingService } from '@core/services/logging.service';

/** Firebase action modes this page handles. */
export type AuthActionMode =
  | 'verifyEmail'
  | 'resetPassword'
  | 'recoverEmail'
  | 'verifyAndChangeEmail';

/** What the page is currently showing. */
export type AuthActionView =
  | 'working'
  | 'verified'
  | 'reset-form'
  | 'reset-done'
  | 'recovered'
  | 'email-changed'
  | 'error';

/**
 * The Firebase `ActionCodeInfo.operation` each mode must carry. The mode is
 * attacker-controllable query text; the operation comes from Firebase, so a
 * code is only consumed when the two agree.
 */
const MODE_OPERATIONS: Readonly<Record<AuthActionMode, string>> = {
  verifyEmail: 'VERIFY_EMAIL',
  resetPassword: 'PASSWORD_RESET',
  recoverEmail: 'RECOVER_EMAIL',
  verifyAndChangeEmail: 'VERIFY_AND_CHANGE_EMAIL',
};

/** Auth-flow routes that must never become a post-login destination. */
const NON_RETURNABLE_PATHS = ['/auth/action', '/login', '/logout', '/logging-in'];

/**
 * Handles the links in Firebase Authentication emails (#835), replacing
 * Google's default page at `<project>.firebaseapp.com/__/auth/action`.
 *
 * Route: `/auth/action?mode=<mode>&oobCode=<code>[&continueUrl=…]`. Public:
 * the person following a link is usually not signed in.
 *
 * - `verifyEmail`: consumes the code; if the same account is signed in here,
 *   reloads it and forces a token refresh so `email_verified` reaches
 *   Firestore rules and the API at once.
 * - `resetPassword`: validates the code first, then sets a new password that
 *   meets the signup policy (15+ characters, no composition rules, #943).
 * - `recoverEmail`: restores the previous email after an unwanted change and
 *   offers a password reset to that address.
 * - `verifyAndChangeEmail`: confirms a requested email change.
 *
 * The code is checked against the Firebase operation before anything is
 * consumed, and the query string (which holds the single-use code) is removed
 * from the address bar and history as soon as it has been read.
 *
 * `continueUrl` is honoured only when it is on this site's own origin and not
 * an auth-flow route, and then only through AuthFlowService's internal-path
 * check (no open redirect).
 */
@Component({
  selector: 'app-auth-action',
  templateUrl: './auth-action.component.html',
  styleUrl: './auth-action.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AuthActionComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly actionCodes = inject(AuthActionCodeService);
  private readonly authFlow = inject(AuthFlowService);
  private readonly logging = inject(LoggingService);
  private readonly platformId = inject(PLATFORM_ID);

  view: AuthActionView = 'working';
  mode: AuthActionMode | null = null;
  /** Email the action concerns, shown to the user. */
  email: string | null = null;
  /** User-facing explanation when `view` is `error`. */
  errorMessage = '';
  /** True when the verified account is the one signed in on this browser. */
  signedInAsVerifiedUser = false;
  /** True while a form or follow-up action is submitting. */
  busy = false;
  /** Feedback after offering a reset email from the recover flow. */
  resetLinkSent = false;
  /** True when the failure was a network error: the code is unused, so retrying works. */
  canRetry = false;

  readonly passwordForm: FormGroup = this.fb.group(
    {
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(15),
        ]),
      ],
      confirm: ['', Validators.required],
    },
    { validators: AuthActionComponent.passwordsMatch },
  );

  private code = '';

  /** Form-level validator: the confirmation must equal the password. */
  static passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirm = group.get('confirm')?.value;
    return password && confirm && password !== confirm ? { mismatch: true } : null;
  }

  ngOnInit(): void {
    // Action codes are single-use: never consume one during SSR.
    if (!isPlatformBrowser(this.platformId)) return;

    const params = this.route.snapshot.queryParamMap;
    const mode = params.get('mode');
    this.code = params.get('oobCode') ?? '';
    this.rememberContinueUrl(params.get('continueUrl'));
    // The single-use code must not linger in the address bar or history
    // while the user fills in the form (or shares a screenshot of it).
    this.location.replaceState(this.location.path().split('?')[0] || '/auth/action');

    if (!this.code || !mode || !(mode in MODE_OPERATIONS)) {
      this.fail('This link is incomplete or not recognised. Request a new email and try again.');
      return;
    }
    this.mode = mode as AuthActionMode;
    void this.run();
  }

  /** Submits the new password for a reset link. */
  async submitNewPassword(): Promise<void> {
    if (this.passwordForm.invalid || this.busy) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.busy = true;
    try {
      await this.actionCodes.confirmPasswordReset(this.code, this.passwordForm.value.password);
      this.passwordForm.reset();
      this.view = 'reset-done';
    } catch (error) {
      this.handleError(error);
    } finally {
      this.busy = false;
    }
  }

  /** After an email recovery, sends a password reset to the restored address. */
  async sendResetToRecoveredEmail(): Promise<void> {
    if (!this.email || this.busy) return;
    this.busy = true;
    try {
      await this.actionCodes.sendPasswordResetEmail(this.email);
      this.resetLinkSent = true;
    } catch (error) {
      // Error code only: Firebase errors can carry the email in customData.
      this.logging.logError('Password reset email after recovery failed', {
        code: (error as { code?: string })?.code,
      });
      this.errorMessage = 'We could not send the reset email. Use "Forgot password" on the sign-in page.';
    } finally {
      this.busy = false;
    }
  }

  /** Retries after a network failure; the action code was not consumed. */
  retry(): Promise<void> {
    if (!this.canRetry) return Promise.resolve();
    this.canRetry = false;
    this.errorMessage = '';
    this.view = 'working';
    return this.run();
  }

  /** Continues into the app (signed in) or to sign-in. */
  async continue(): Promise<void> {
    await this.router.navigate([this.signedInAsVerifiedUser ? '/logging-in' : '/login'], {
      replaceUrl: true,
    });
  }

  /** Error helpers for the password field. */
  passwordError(key: string): boolean {
    const control = this.passwordForm.get('password');
    return !!control && (control.dirty || control.touched) && control.hasError(key);
  }

  private async run(): Promise<void> {
    if (!this.mode) return;
    try {
      // Read (never consume) the code first, and refuse it unless Firebase
      // says it is for the operation the link's mode claims.
      const info = await this.actionCodes.checkActionCode(this.code);
      if (info.operation !== MODE_OPERATIONS[this.mode]) {
        this.fail('This link does not match the action it is for. Request a new email and try again.');
        return;
      }
      this.email = info.data.email ?? null;

      switch (this.mode) {
        case 'verifyEmail':
          await this.actionCodes.applyActionCode(this.code);
          await this.refreshSignedInUser();
          this.view = 'verified';
          break;
        case 'resetPassword':
          // Consumed later, by confirmPasswordReset.
          this.view = 'reset-form';
          break;
        case 'recoverEmail':
          await this.actionCodes.applyActionCode(this.code);
          this.view = 'recovered';
          break;
        case 'verifyAndChangeEmail':
          await this.actionCodes.applyActionCode(this.code);
          this.view = 'email-changed';
          break;
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * After verification, if the verified account is the one signed in here,
   * reload it so the app sees it as verified without signing in again.
   */
  private async refreshSignedInUser(): Promise<void> {
    const user = this.actionCodes.currentUser();
    if (user && this.email && user.email?.toLowerCase() === this.email.toLowerCase()) {
      try {
        await user.reload();
        // Force a fresh ID token so email_verified=true reaches Firestore
        // rules and the API immediately, not after the hourly refresh.
        await user.getIdToken(true);
        this.signedInAsVerifiedUser = true;
      } catch (error) {
        // Verification itself succeeded; the user just signs in again.
        this.logging.logWarning('Token refresh after email verification failed', {
          code: (error as { code?: string })?.code,
        });
      }
    }
  }

  private handleError(error: unknown): void {
    const code = (error as { code?: string })?.code ?? '';
    switch (code) {
      case 'auth/expired-action-code':
        this.fail('This link has expired. Request a new email and try again.');
        break;
      case 'auth/invalid-action-code':
        this.fail('This link has already been used or is not valid. Request a new email and try again.');
        break;
      case 'auth/user-disabled':
        this.fail('This account has been disabled. Contact support@scanninja.ai for help.');
        break;
      case 'auth/user-not-found':
        this.fail('We could not find the account for this link.');
        break;
      case 'auth/network-request-failed':
        // Nothing reached Firebase, so the link is still good: offer a retry
        // rather than sending the user off for a new email.
        this.fail('We could not reach the sign-in service. Check your connection and try again.');
        this.canRetry = true;
        break;
      case 'auth/weak-password':
        // Stay on the form: the code is still valid.
        this.passwordForm.get('password')?.setErrors({ weak: true });
        break;
      default:
        this.logging.logError('Auth action failed', { mode: this.mode, code });
        this.fail('Something went wrong. Please try again, or request a new email.');
    }
  }

  private fail(message: string): void {
    this.canRetry = false;
    this.errorMessage = message;
    this.view = 'error';
  }

  /**
   * Firebase passes `continueUrl` as an absolute URL. Keep only its path, and
   * only when it is on this origin; AuthFlowService re-checks it is internal.
   */
  private rememberContinueUrl(continueUrl: string | null): void {
    if (!continueUrl) return;
    try {
      const url = new URL(continueUrl, window.location.origin);
      if (url.origin !== window.location.origin) return;
      // Returning to an auth-flow page (e.g. this consumed link) after sign-in
      // would show an error instead of entering the app.
      if (NON_RETURNABLE_PATHS.some((p) => url.pathname === p || url.pathname.startsWith(`${p}/`))) {
        return;
      }
      this.authFlow.rememberRedirectUrl(`${url.pathname}${url.search}${url.hash}`);
    } catch {
      // Malformed: ignore.
    }
  }
}
